import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Bot, ChevronRight, RotateCcw } from "lucide-react";
import type { Agent } from "@/hooks/use-agents";
import { useSubAgentSessionState } from "@/hooks/use-sub-agent-session-state";
import type {
  PendingSubAgentSpawn,
  SubAgentThreadReference,
} from "@/lib/sub-agent-thread";
import { cn } from "@/lib/utils";
import { SubAgentStateBadge } from "@/components/sub-agent-state-badge";
import { Button } from "@/components/ui/button";
import { Item } from "@/components/ui/item";

interface SubAgentToolCardCommonProps {
  allAgents: readonly Agent[];
  className?: string;
}

export type SubAgentToolCardProps = SubAgentToolCardCommonProps & (
  | {
      reference: SubAgentThreadReference;
      pending?: never;
      fetchWithAuth: (url: string, init: RequestInit) => Promise<Response>;
      onOpen: (reference: SubAgentThreadReference) => void;
    }
  | {
      pending: PendingSubAgentSpawn;
      reference?: never;
      fetchWithAuth?: never;
      onOpen?: never;
    }
);

type ResolvedSubAgentToolCardProps = Extract<
  SubAgentToolCardProps,
  { reference: SubAgentThreadReference }
>;

function isResolvedCard(
  props: SubAgentToolCardProps,
): props is ResolvedSubAgentToolCardProps {
  return props.reference !== undefined;
}

const REASON_LABEL_KEYS: Record<NonNullable<SubAgentThreadReference["reasonCode"]>, string> = {
  not_persisted: "subAgent.reason.notPersisted",
  parent_unavailable: "subAgent.reason.parentUnavailable",
  session_conflict: "subAgent.reason.sessionConflict",
  reservation_failed: "subAgent.reason.reservationFailed",
  scheduler_failed: "subAgent.reason.schedulerFailed",
};

export function SubAgentToolCard(props: SubAgentToolCardProps) {
  if (isResolvedCard(props)) return <ResolvedSubAgentToolCard {...props} />;
  return (
    <PendingSubAgentToolCard
      pending={props.pending}
      allAgents={props.allAgents}
      className={props.className}
    />
  );
}

function PendingSubAgentToolCard({
  pending,
  allAgents,
  className,
}: SubAgentToolCardCommonProps & { pending: PendingSubAgentSpawn }) {
  const displayName = allAgents.find((agent) => agent.id === pending.agentId)?.name
    ?? pending.agentId;

  return (
    <div
      className={cn("my-3 flex max-w-xl items-center gap-1.5", className)}
      data-testid="sub-agent-tool-card"
      data-state="starting"
      data-view-available="pending"
    >
      <Item
        variant="outline"
        size="sm"
        className="min-w-0 flex-1 flex-nowrap gap-3 bg-card px-3 py-2.5 text-card-foreground shadow-sm"
        aria-disabled="true"
      >
        <SubAgentCardContent
          displayName={displayName}
          promptSummary={pending.promptSummary}
          badge={<SubAgentStateBadge state="queued" starting />}
        />
      </Item>
    </div>
  );
}

function ResolvedSubAgentToolCard({
  reference,
  allAgents,
  fetchWithAuth,
  onOpen,
  className,
}: SubAgentToolCardCommonProps & {
  reference: SubAgentThreadReference;
  fetchWithAuth: (url: string, init: RequestInit) => Promise<Response>;
  onOpen: (reference: SubAgentThreadReference) => void;
}) {
  const { t } = useTranslation();
  const recordedRenderRef = useRef<string | null>(null);
  const snapshot = useSubAgentSessionState(reference, fetchWithAuth, {
    enabled: reference.viewAvailable,
  });
  const displayName = allAgents.find((agent) => agent.id === reference.agentId)?.name
    ?? reference.agentId;
  const unavailableReason = !reference.viewAvailable && reference.reasonCode
    ? t(REASON_LABEL_KEYS[reference.reasonCode])
    : null;
  const promptSummary = reference.viewAvailable
    && reference.reasonCode === "session_conflict"
    ? t("subAgent.continuedTranscript")
    : unavailableReason
      ?? (snapshot.staleEmpty
        ? t("subAgent.noOutputMayHaveFailed")
        : reference.promptSummary ?? t("subAgent.transcript"));
  const remoteUnavailable = snapshot.availability !== "available";

  useEffect(() => {
    if (recordedRenderRef.current === reference.taskId) return;
    recordedRenderRef.current = reference.taskId;
    console.info("[sub-agent-thread] card rendered", {
      taskId: reference.taskId,
      childSessionId: reference.childSessionId,
      viewAvailable: reference.viewAvailable,
    });
  }, [reference.childSessionId, reference.taskId, reference.viewAvailable]);

  const content = (
    <SubAgentCardContent
      displayName={displayName}
      promptSummary={promptSummary}
      badge={(
        <SubAgentStateBadge
          state={snapshot.state}
          availability={snapshot.availability}
          staleEmpty={snapshot.staleEmpty}
        />
      )}
    />
  );

  return (
    <div
      className={cn("my-3 flex max-w-xl items-center gap-1.5", className)}
      data-testid="sub-agent-tool-card"
      data-view-available={reference.viewAvailable}
    >
      {reference.viewAvailable ? (
        <Item
          asChild
          variant="outline"
          size="sm"
          className={cn(
            "min-w-0 flex-1 flex-nowrap gap-3 bg-card px-3 py-2.5 text-card-foreground shadow-sm",
            "hover:border-primary/40 hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <button
            type="button"
            onClick={() => {
              console.info("[sub-agent-thread] panel requested", {
                taskId: reference.taskId,
                childSessionId: reference.childSessionId,
              });
              onOpen(reference);
            }}
            aria-label={t("subAgent.openTranscriptFor", { name: displayName })}
          >
            {content}
            <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
          </button>
        </Item>
      ) : (
        <Item
          variant="outline"
          size="sm"
          className="min-w-0 flex-1 flex-nowrap gap-3 bg-muted/30 px-3 py-2.5 text-muted-foreground shadow-none"
          aria-disabled="true"
        >
          {content}
        </Item>
      )}
      {reference.viewAvailable && remoteUnavailable && (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="size-8 shrink-0"
          onClick={snapshot.retry}
          aria-label={t("subAgent.retryStatus")}
        >
          <RotateCcw aria-hidden="true" className="size-3.5" />
        </Button>
      )}
    </div>
  );
}

function SubAgentCardContent({
  displayName,
  promptSummary,
  badge,
}: {
  displayName: string;
  promptSummary: string;
  badge: ReactNode;
}) {
  return (
    <>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Bot aria-hidden="true" className="size-4" />
      </span>
      <span className="min-w-0 flex-1 text-left">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-semibold">{displayName}</span>
          {badge}
        </span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
          {promptSummary}
        </span>
      </span>
    </>
  );
}
